import React, { useState, useMemo } from 'react'
import moment from 'moment'
import { RiLoader5Fill } from 'react-icons/ri'
import cx from 'classnames'

import {
  formatNumber,
  CHART_HEIGHT,
  YAXIS_WIDTH,
  COLORS,
  tooltipFormatter,
  tooltipLabelFormatter,
  tooltipLabelFormatterUnits,
  tooltipFormatterNumber,
  yaxisFormatter,
  yaxisFormatterNumber,
  GREEN,
  RED,
  convertToPercents,
} from '../helpers'

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts'

import ChartWrapper from '../components/ChartWrapper'
import DateRangeSelect from '../components/DateRangeSelect'
import VolumeChart from '../components/VolumeChart'
import FeesChart from '../components/FeesChart'
import GenericChart from '../components/GenericChart'
import TradersProfitLossChart from '../components/TradersProfitLossChart'

import {
  useVolumeDataBesc,
  useFeesDataBesc,
  useUsersDataBesc,
  useTradersDataBesc,
  usePoolDataBesc,
  useBescSummary,
  useFundingRatesDataBesc,
  useLiquidationsDataBesc,
  useOiByMarketBesc,
} from '../dataProviderBesc'

const NOW = Math.floor(Date.now() / 1000)

const dateRangeOptions = [
  { label: 'Last Month',    id: 1 },
  { label: 'Last 2 Months', id: 2, isDefault: true },
  { label: 'Last 3 Months', id: 3 },
  { label: 'All time',      id: 4 },
]

export default function BESC(props) {
  const [dataRange, setDataRange] = useState({
    fromValue: moment().subtract(3, 'month').toDate(),
    toValue: null,
  })

  const { mode } = props

  const from = dataRange.fromValue ? Math.floor(+new Date(dataRange.fromValue) / 1000) : undefined
  const to   = dataRange.toValue   ? Math.floor(+new Date(dataRange.toValue)   / 1000) : NOW

  const [volumeData,       volumeLoading]    = useVolumeDataBesc({ from, to })
  const [feesData,         feesLoading]      = useFeesDataBesc({ from, to })
  const [usersData,        usersLoading]     = useUsersDataBesc({ from, to })
  const [tradersData,      tradersLoading]   = useTradersDataBesc({ from, to })
  const [poolData,         poolLoading]      = usePoolDataBesc({ from, to })
  const [summary,          summaryLoading]   = useBescSummary()
  const [fundingData,      fundingLoading]   = useFundingRatesDataBesc({ from })
  const [liquidationsData, liqLoading]       = useLiquidationsDataBesc({ from, to })
  const [oiByMarket,       oiMarketLoading]  = useOiByMarketBesc()

  const onDateRangeChange = (dates) => {
    const [start, end] = dates
    setDataRange({ fromValue: start, toValue: end })
  }

  return (
    <div className="Home">
      <div className="page-title-section">
        <div className="page-title-block">
          <h1>Analytics / BESC Hyperchain</h1>
          <p className="page-description">
            Live data via MoneyX Indexer
          </p>
        </div>
        <div className="form">
          <DateRangeSelect
            options={dateRangeOptions}
            startDate={dataRange.fromValue}
            endDate={dataRange.toValue}
            onChange={onDateRangeChange}
          />
        </div>
      </div>

      <div className="chart-grid">

        {/* ── TVL stat ───────────────────────────────────────────────────────── */}
        <div className="chart-cell stats">
          {!summaryLoading ? (
            <>
              <div className="total-stat-label">TVL</div>
              <div className="total-stat-value">
                {formatNumber(summary.tvl, { currency: true })}
                <span className="total-stat-delta" style={{ color: GREEN }}>
                  MLP {formatNumber(summary.totalAum, { currency: true, compact: true })}
                </span>
                <span className="total-stat-delta" style={{ color: GREEN }}>
                  {' '}Staked {formatNumber(summary.moneyStakedUsd, { currency: true, compact: true })}
                </span>
              </div>
            </>
          ) : null}
          {summaryLoading && <RiLoader5Fill size="3em" className="loader" />}
        </div>

        {/* ── MONEY stat ─────────────────────────────────────────────────────── */}
        <div className="chart-cell stats">
          {!summaryLoading ? (
            <>
              <div className="total-stat-label">MONEY</div>
              <div className="total-stat-value">
                {formatNumber(summary.moneyPrice, { currency: true })}
                <span className="total-stat-delta" style={{ color: GREEN }}>
                  MCap {formatNumber(summary.moneyMarketCap, { currency: true, compact: true })}
                </span>
                <span className="total-stat-delta" style={{ color: GREEN }}>
                  {' '}Staked {formatNumber(summary.moneyStaked, { compact: true })}
                </span>
              </div>
            </>
          ) : null}
          {summaryLoading && <RiLoader5Fill size="3em" className="loader" />}
        </div>

        {/* ── Total Volume stat ──────────────────────────────────────────────── */}
        <div className="chart-cell stats">
          {!summaryLoading ? (
            <>
              <div className="total-stat-label">Total Volume</div>
              <div className="total-stat-value">
                {formatNumber(summary.totalVolume, { currency: true })}
                {!!summary.dailyVolume && (
                  <span className="total-stat-delta plus" title="24h volume">
                    +{formatNumber(summary.dailyVolume, { currency: true, compact: true })}
                  </span>
                )}
              </div>
            </>
          ) : null}
          {summaryLoading && <RiLoader5Fill size="3em" className="loader" />}
        </div>

        {/* ── Total Fees stat ────────────────────────────────────────────────── */}
        <div className="chart-cell stats">
          {!summaryLoading ? (
            <>
              <div className="total-stat-label">Total Fees</div>
              <div className="total-stat-value">
                {formatNumber(summary.totalFees, { currency: true })}
                {!!summary.weeklyFees && (
                  <span className="total-stat-delta plus" title="7-day fees">
                    +{formatNumber(summary.weeklyFees, { currency: true, compact: true })}
                  </span>
                )}
              </div>
            </>
          ) : null}
          {summaryLoading && <RiLoader5Fill size="3em" className="loader" />}
        </div>

        {/* ── Open Interest stat ─────────────────────────────────────────────── */}
        <div className="chart-cell stats">
          {!summaryLoading ? (
            <>
              <div className="total-stat-label">Open Interest</div>
              <div className="total-stat-value">
                {formatNumber(summary.longOI + summary.shortOI, { currency: true })}
                <span className="total-stat-delta" style={{ color: GREEN }}>
                  L {formatNumber(summary.longOI, { currency: true, compact: true })}
                </span>
                <span className="total-stat-delta" style={{ color: RED }}>
                  {' '}S {formatNumber(summary.shortOI, { currency: true, compact: true })}
                </span>
              </div>
            </>
          ) : null}
          {summaryLoading && <RiLoader5Fill size="3em" className="loader" />}
        </div>

        {/* ── MLP Pool stat ─────────────────────────────────────────────────── */}
        <div className="chart-cell stats">
          {!summaryLoading ? (
            <>
              <div className="total-stat-label">MLP Pool</div>
              <div className="total-stat-value">
                {formatNumber(summary.totalAum, { currency: true })}
                {summary.aumDelta !== 0 && (
                  <span
                    className={cx('total-stat-delta', summary.aumDelta > 0 ? 'plus' : 'minus')}
                    title="Change since previous day"
                  >
                    {summary.aumDelta > 0 ? '+' : ''}{formatNumber(summary.aumDelta, { currency: true, compact: true })}
                  </span>
                )}
              </div>
            </>
          ) : null}
          {summaryLoading && <RiLoader5Fill size="3em" className="loader" />}
        </div>

        {/* ── Total Users stat ───────────────────────────────────────────────── */}
        <div className="chart-cell stats">
          {!summaryLoading ? (
            <>
              <div className="total-stat-label">Total Users</div>
              <div className="total-stat-value">
                {formatNumber(summary.totalUsers)}
                {!!summary.activeUsers && (
                  <span className="total-stat-delta plus" title="Active last 30 days">
                    +{formatNumber(summary.activeUsers)}
                  </span>
                )}
              </div>
            </>
          ) : null}
          {summaryLoading && <RiLoader5Fill size="3em" className="loader" />}
        </div>

        {/* ── Volume chart ───────────────────────────────────────────────────── */}
        <div className="chart-cell">
          <VolumeChart
            data={volumeData}
            loading={volumeLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
          />
        </div>

        {/* ── Fees chart ─────────────────────────────────────────────────────── */}
        <div className="chart-cell">
          <FeesChart
            data={feesData}
            loading={feesLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
            xaxisTickFormatter={tooltipLabelFormatter}
            tooltipLabelFormatter={tooltipLabelFormatter}
          />
        </div>

        {/* ── Pool Composition chart ────────────────────────────────────────── */}
        <div className="chart-cell">
          <GenericChart
            syncId="bescPool"
            loading={poolLoading}
            title="Pool Composition"
            data={poolData?.data}
            controls={{ convertToPercents }}
            yaxisDataKey="all"
            items={(poolData?.tokenSymbols || []).map((sym, i) => ({
              key: sym,
              color: COLORS[i % COLORS.length],
              fillOpacity: 0.5,
            }))}
            type="Area"
          />
        </div>

        {/* ── Traders Net P&L chart ──────────────────────────────────────────── */}
        <div className="chart-cell">
          <ChartWrapper
            title="Traders Net PnL"
            loading={tradersLoading}
            data={tradersData?.data}
            csvFields={[
              { key: 'pnl', name: 'Net PnL' },
              { key: 'pnlCumulative', name: 'Cumulative PnL' },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <ComposedChart data={tradersData?.data} syncId="bescTraders">
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis dataKey="timestamp" tickFormatter={tooltipLabelFormatter} minTickGap={30} />
                <YAxis
                  domain={[
                    -(tradersData?.stats?.maxAbsCumulativePnl || 0) * 1.05,
                     (tradersData?.stats?.maxAbsCumulativePnl || 0) * 1.05,
                  ]}
                  orientation="right"
                  yAxisId="right"
                  tickFormatter={yaxisFormatter}
                  width={YAXIS_WIDTH}
                  tick={{ fill: COLORS[4] }}
                />
                <YAxis
                  domain={[
                    -(tradersData?.stats?.maxAbsPnl || 0) * 1.05,
                     (tradersData?.stats?.maxAbsPnl || 0) * 1.05,
                  ]}
                  tickFormatter={yaxisFormatter}
                  width={YAXIS_WIDTH}
                />
                <Tooltip
                  formatter={tooltipFormatter}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />
                <Bar type="monotone" dataKey="pnl" name="Net PnL" dot={false}>
                  {(tradersData?.data || []).map((item, i) => (
                    <Cell key={`cell-${i}`} fill={item.pnl > 0 ? GREEN : RED} />
                  ))}
                </Bar>
                <Line
                  type="monotone"
                  strokeWidth={2}
                  stroke={COLORS[4]}
                  dataKey="currentPnlCumulative"
                  name="Cumulative PnL"
                  yAxisId="right"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="chart-description">
              <p>Considers settled (closed) positions. Fees not factored into PnL.</p>
            </div>
          </ChartWrapper>
        </div>

        {/* ── Traders Profit/Loss distribution ──────────────────────────────── */}
        <div className="chart-cell">
          <TradersProfitLossChart
            syncId="bescTraders"
            loading={tradersLoading}
            tradersData={tradersData}
            yaxisWidth={YAXIS_WIDTH}
            chartHeight={CHART_HEIGHT}
          />
        </div>

        {/* ── Open Interest over time ────────────────────────────────────────── */}
        <div className="chart-cell">
          <GenericChart
            syncId="bescTraders"
            loading={tradersLoading}
            title="Open Interest"
            data={tradersData?.data?.map(item => ({ all: item.openInterest, ...item }))}
            controls={{ convertToPercents }}
            yaxisDataKey="all"
            items={[
              { key: 'shortOpenInterest', name: 'Short', color: RED },
              { key: 'longOpenInterest',  name: 'Long',  color: GREEN },
            ]}
            type="Bar"
          />
        </div>

        {/* ── Unique Users chart ─────────────────────────────────────────────── */}
        <div className="chart-cell">
          <GenericChart
            syncId="bescUsers"
            loading={usersLoading}
            title="Unique Users"
            data={usersData}
            yaxisDataKey="uniqueCount"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'uniqueCount', name: 'Active Users' },
            ]}
            type="Composed"
          />
        </div>

        {/* ── New Users chart ────────────────────────────────────────────────── */}
        <div className="chart-cell">
          <GenericChart
            syncId="bescUsers"
            loading={usersLoading}
            title="New Users"
            data={usersData?.map(item => ({ ...item, all: item.newCount }))}
            yaxisDataKey="newCount"
            rightYaxisDataKey="uniqueCountCumulative"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'newCount', name: 'New Users' },
              {
                key: 'uniqueCountCumulative',
                name: 'Cumulative',
                type: 'Line',
                yAxisId: 'right',
                strokeWidth: 2,
                color: COLORS[4],
              },
            ]}
            type="Composed"
          />
        </div>

        {/* ── Funding Rate History ───────────────────────────────────────────── */}
        <div className="chart-cell">
          <ChartWrapper
            title="Funding Rate (Annualised %)"
            loading={fundingLoading}
            data={fundingData}
            csvFields={[
              { key: 'hourlyRate', name: 'Hourly Rate' },
              { key: 'annualRate', name: 'Annual Rate' },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <ComposedChart data={fundingData} syncId="bescFunding">
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis dataKey="snap_ts" tickFormatter={tooltipLabelFormatter} minTickGap={30} />
                <YAxis tickFormatter={(v) => `${(v * 100).toFixed(2)}%`} width={YAXIS_WIDTH} />
                <Tooltip
                  formatter={(v, name) => [`${(v * 100).toFixed(4)}%`, name]}
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="annualRate"
                  name="Annual Funding Rate"
                  stroke={COLORS[2]}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="chart-description">
              <p>Annualised hourly funding rate derived from on-chain cumulative snapshots.</p>
            </div>
          </ChartWrapper>
        </div>

        {/* ── Liquidations History ───────────────────────────────────────────── */}
        <div className="chart-cell">
          <ChartWrapper
            title="Daily Liquidations"
            loading={liqLoading}
            data={liquidationsData}
            csvFields={[
              { key: 'liquidation_count', name: 'Count' },
              { key: 'volume_usd', name: 'Volume USD' },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <ComposedChart data={liquidationsData} syncId="bescLiq">
                <CartesianGrid strokeDasharray="10 10" />
                <XAxis dataKey="day_ts" tickFormatter={tooltipLabelFormatter} minTickGap={30} />
                <YAxis yAxisId="left" tickFormatter={yaxisFormatterNumber} width={YAXIS_WIDTH} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={yaxisFormatter} width={YAXIS_WIDTH} />
                <Tooltip
                  formatter={(v, name) =>
                    name === 'Volume (USD)'
                      ? [`$${formatNumber(Number(v) / 1e30, { compact: true })}`, name]
                      : [v, name]
                  }
                  labelFormatter={tooltipLabelFormatter}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="liquidation_count" name="Count" fill={RED} opacity={0.75} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey={(d) => Number(d.volume_usd) / 1e30}
                  name="Volume (USD)"
                  stroke={COLORS[4]}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartWrapper>
        </div>

        {/* ── Open Interest by Market ────────────────────────────────────────── */}
        <div className="chart-cell">
          <ChartWrapper
            title="Open Interest by Market"
            loading={oiMarketLoading}
            data={oiByMarket}
            csvFields={[
              { key: 'market',  name: 'Market' },
              { key: 'longOI',  name: 'Long OI' },
              { key: 'shortOI', name: 'Short OI' },
            ]}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <ComposedChart data={oiByMarket} layout="vertical">
                <CartesianGrid strokeDasharray="10 10" horizontal={false} />
                <XAxis type="number" tickFormatter={yaxisFormatter} />
                <YAxis type="category" dataKey="market" width={60} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, name) => [formatNumber(v, { currency: true, compact: true }), name]}
                  contentStyle={{ textAlign: 'left' }}
                />
                <Legend />
                <Bar dataKey="longOI"  name="Long OI"  stackId="oi" fill={GREEN} opacity={0.8} />
                <Bar dataKey="shortOI" name="Short OI" stackId="oi" fill={RED}   opacity={0.8} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartWrapper>
        </div>

      </div>
    </div>
  )
}
